import React, { useCallback, useEffect, useState } from "react";
import {
  MdPeople,
  MdHome,
  MdCreditCard,
  MdSchool,
  MdHowToVote,
  MdChildCare,
  MdAttachMoney,
} from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Skeleton from "components/skeleton/Skeleton";
import StatCard from "components/stat-card";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { formatCurrency, formatDate, formatNumber } from "utils/format";
import { getAggregatedDemographics, getEnrichmentStatus } from "services/dataAxleService";

import AgeDistributionChart from "./components/AgeDistributionChart";
import GenderDistributionChart from "./components/GenderDistributionChart";
import MaritalStatusChart from "./components/MaritalStatusChart";
import HouseholdIncomeChart from "./components/HouseholdIncomeChart";
import HomeownershipChart from "./components/HomeownershipChart";

const TITLE = "Insights";

const formatThousands = (value) =>
  formatCurrency(value || 0, { notation: "compact", maximumFractionDigits: 0 });

const getPercentage = (value, total) => (total > 0 ? ((value / total) * 100).toFixed(1) : 0);

const EnrichmentInfo = ({ status, enrichedCount }) => (
  <div className="flex flex-col gap-1 py-1">
    <p className="font-semibold">Data Enrichment Status</p>
    <p>{formatNumber(status.totalMembers || 0)} total members in database</p>
    <p>{formatNumber(status.withCoordinates || 0)} members have location data</p>
    <p>{formatNumber(enrichedCount)} members enriched with demographics</p>
  </div>
);

const ChartCard = ({ title, className = "", children }) => (
  <Card padding="md" className={`gap-4 ${className}`}>
    <CardHeader title={title} />
    {children}
  </Card>
);

const LoadingState = () => (
  <>
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton key={i} className="h-28 rounded-card" />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
      <Skeleton className="h-96 rounded-card lg:col-span-2" />
      <Skeleton className="h-96 rounded-card" />
    </div>
  </>
);

const Insights = () => {
  const [demographics, setDemographics] = useState(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    // The service resolves `{ success: false }` instead of throwing on request errors.
    const [demoResponse, statusResponse] = await Promise.all([
      getAggregatedDemographics(),
      getEnrichmentStatus(),
    ]);

    if (demoResponse?.success) {
      setDemographics(demoResponse.data);
    } else {
      setFailed(true);
    }
    if (statusResponse?.success) {
      setEnrichmentStatus(statusResponse.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title={TITLE} description="Church demographics and household insights" />
        <LoadingState />
      </PageContainer>
    );
  }

  if (failed) {
    return (
      <PageContainer>
        <PageHeader title={TITLE} description="Church demographics and household insights" />
        <Card padding="md">
          <ErrorState
            title="Couldn't Load Insights"
            message="Demographic data is unavailable right now. Please try again."
            onRetry={fetchData}
          />
        </Card>
      </PageContainer>
    );
  }

  const totalMembers = demographics?.totalEnriched || 0;

  if (!demographics || totalMembers === 0) {
    return (
      <PageContainer>
        <PageHeader title={TITLE} description="Church demographics and household insights" />
        <Card padding="md">
          <EmptyState
            icon={MdPeople}
            title="No Demographic Data Yet"
            description="Demographic insights will appear here once your member data has been enriched. Contact our sales team to enable this feature for your organization."
          />
        </Card>
      </PageContainer>
    );
  }

  const avgHouseholdSize = demographics.financial?.averageHouseholdSize?.toFixed(1) || 0;
  const avgIncome = demographics.financial?.averageIncome || 0;
  const medianIncome = demographics.financial?.medianIncome || 0;
  const avgHomeValue = demographics.financial?.averageHomeValue || 0;
  const collegeEducatedRate = demographics.education?.collegeEducatedRate || 0;
  const voterRegistrationRate = demographics.civic?.voterRegistrationRate || 0;
  const homeownershipRate = demographics.homeownership?.ownershipRate || 0;
  const enrichmentRate = enrichmentStatus?.enrichmentRate || 0;

  return (
    <PageContainer>
      <PageHeader
        title={TITLE}
        description={`Church demographics based on ${formatNumber(totalMembers)} enriched member profiles`}
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={MdPeople}
          label="Total Members"
          value={formatNumber(totalMembers)}
          caption={
            enrichmentStatus
              ? `With demographic data · ${enrichmentRate}% enrichment rate`
              : "With demographic data"
          }
          info={
            enrichmentStatus ? (
              <EnrichmentInfo status={enrichmentStatus} enrichedCount={totalMembers} />
            ) : undefined
          }
        />
        <StatCard
          icon={MdHome}
          label="Average Household Size"
          value={avgHouseholdSize}
          caption="People per household"
        />
        <StatCard
          icon={MdAttachMoney}
          label="Average Income"
          value={formatThousands(avgIncome)}
          caption={`Median: ${formatThousands(medianIncome)}`}
        />
        <StatCard
          icon={MdHome}
          label="Homeownership"
          value={`${homeownershipRate}%`}
          caption="Own their home"
        />
        <StatCard
          icon={MdCreditCard}
          label="Home Value (Avg)"
          value={formatThousands(avgHomeValue)}
          caption="Average home value"
        />
        <StatCard
          icon={MdSchool}
          label="Education (College+)"
          value={`${collegeEducatedRate}%`}
          caption="Bachelor degree or higher"
        />
        <StatCard
          icon={MdHowToVote}
          label="Voter Registration"
          value={`${voterRegistrationRate}%`}
          caption="Registered voters"
        />
        <StatCard
          icon={MdChildCare}
          label="Families with Children"
          value={`${getPercentage(demographics.family?.householdsWithChildren, totalMembers)}%`}
          caption={`Avg ${demographics.family?.averageChildrenPerHousehold?.toFixed(1) || 0} children`}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
        <ChartCard title="Age Distribution" className="lg:col-span-2">
          <AgeDistributionChart data={demographics.ageDistribution} />
        </ChartCard>
        <ChartCard title="Gender Distribution">
          <GenderDistributionChart data={demographics.genderDistribution} />
        </ChartCard>
        <ChartCard title="Marital Status">
          <MaritalStatusChart data={demographics.maritalStatus} />
        </ChartCard>
        <ChartCard title="Household Income Range" className="lg:col-span-2">
          <HouseholdIncomeChart data={demographics.incomeDistribution} />
        </ChartCard>
        <ChartCard title="Homeownership">
          <HomeownershipChart data={demographics.homeownership} />
        </ChartCard>
      </div>

      <p className="text-xs text-fg-muted">
        Demographic insights are estimates based on available member data and may vary from actual
        member information. Last updated: {formatDate(new Date())}
      </p>
    </PageContainer>
  );
};

export default Insights;
